import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import MasterLayout from "../masterLayout/MasterLayout";
import Breadcrumb from "../components/Breadcrumb";
import KkpPlanningPageActions from "../components/kkp/KkpPlanningPageActions";
import KkpPlanningEmptyState from "../components/kkp/KkpPlanningEmptyState";
import gandiService from "../services/gandiService";
import { ROUTES } from "../config/routes";
import { 
  getDaysUntilExpiration,
  getRenewalStateClass,
  getRenewalStateText,
  getStatusClass,
  getStatusText,
  getDaysRemainingClass,
  getDaysRemainingText
} from "../utils/domainUtils";
import {
  getGandiExpirationDate,
  getGandiDomainStatus,
  getGandiRenewalState,
} from "../utils/gandiDomainUtils";
import { AppAlert } from "../components/ui";
import "../styles/kkp-planning-page.scss";

const DomaineDetailGandiPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [domain, setDomain] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [apiWorking, setApiWorking] = useState(true);

  // Fonction pour formater la date
  const formatDate = useCallback((dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }, []);

  // Charger les détails du domaine
  useEffect(() => {
    const loadDomainDetails = async () => {
      try {
        setLoading(true);
        setError(null);

        if (!gandiService.isConfigured()) {
          setError('Service API non configuré');
          setLoading(false);
          return;
        }

        const domainData = await gandiService.getDomainDetails(parseInt(id));
        setDomain(domainData);
        setApiWorking(true);

      } catch (err) {
        console.error('Erreur lors du chargement du domaine:', err);
        setError(err.message || 'Erreur lors du chargement du domaine');
        setApiWorking(false);
        
        // Données de test en cas d'erreur
        const testDomain = {
          id: parseInt(id),
          fqdn: `test-gandi-${id}.fr`,
          organization: 'org4',
          status: ['clientTransferProhibited'],
          autoRenew: true,
          dateCreated: '2020-03-16T09:40:08Z',
          dateRegistryEnd: '2026-03-16T08:40:08Z',
          dateUpdated: '2025-02-13T07:55:26Z',
          nameservers: ['a.dns.gandi.net', 'b.dns.gandi.net', 'c.dns.gandi.net'],
          contacts: {
            owner: {
              orgname: 'Test Organization',
              family: 'Test',
              given: 'User',
              email: 'test@example.com',
              phone: '+33.123456789'
            }
          },
          dnsKeys: [],
          glueRecords: [],
          livednsInfo: {
            nameservers: ['a.dns.gandi.net', 'b.dns.gandi.net', 'c.dns.gandi.net'],
            current: 'classic',
            dnssec_available: true,
            livednssec_available: true
          },
          restoreInfo: null,
          tags: [],
          webRedirections: [],
          rawData: {
            basic: {
              fqdn: `test-gandi-${id}.fr`,
              tld: 'fr',
              status: ['clientTransferProhibited'],
              autorenew: {
                enabled: true,
                duration: 1
              }
            }
          },
          lastSync: '2025-10-15 09:40:55'
        };
        setDomain(testDomain);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      loadDomainDetails();
    }
  }, [id]);

  // Calculer les informations dérivées
  const domainInfo = useMemo(() => {
    if (!domain) return null;

    const status = getGandiDomainStatus(domain);
    const days = getDaysUntilExpiration(domain.dateRegistryEnd);
    const renewalState = getGandiRenewalState(domain);
    
    return {
      ...domain,
      status,
      daysRemaining: days,
      renewalState,
      statusClass: getStatusClass(status),
      statusText: getStatusText(status),
      daysClass: getDaysRemainingClass(days),
      daysText: getDaysRemainingText(days),
      renewalClass: getRenewalStateClass(renewalState),
      renewalText: getRenewalStateText(renewalState)
    };
  }, [domain]);

  // Fonction pour parser les données JSON
  const parseJsonData = (data) => {
    if (!data) return null;
    if (typeof data === 'string') {
      try {
        return JSON.parse(data);
      } catch (e) {
        return null;
      }
    }
    return data;
  };

  const backToList = () => navigate(ROUTES.iti.domainesGandi);

  const toolsMenu = (
    <KkpPlanningPageActions
      actions={[
        {
          key: 'back',
          label: 'Retour à la liste',
          icon: 'mdi:arrow-left',
          onClick: backToList,
        },
      ]}
    />
  );

  if (loading) {
    return (
      <MasterLayout>
        <Breadcrumb
          title="Domaine Gandi"
          subtitle="Détail domaine Gandi"
          actions={toolsMenu}
        />
        <KkpPlanningEmptyState loading />
      </MasterLayout>
    );
  }

  if (error && !domain) {
    return (
      <MasterLayout>
        <Breadcrumb
          title="Domaine Gandi"
          subtitle="Détail domaine Gandi"
          actions={toolsMenu}
        />
        <KkpPlanningEmptyState
          message={error}
          actions={[
            {
              key: 'back',
              label: 'Retour à la liste',
              icon: 'mdi:arrow-left',
              onClick: backToList,
              variant: 'primary',
            },
          ]}
        />
      </MasterLayout>
    );
  }

  if (!domainInfo) {
    return (
      <MasterLayout>
        <Breadcrumb
          title="Domaine Gandi"
          subtitle="Détail domaine Gandi"
          actions={toolsMenu}
        />
        <KkpPlanningEmptyState
          message="Domaine non trouvé"
          actions={[
            {
              key: 'back',
              label: 'Retour à la liste',
              icon: 'mdi:arrow-left',
              onClick: backToList,
              variant: 'primary',
            },
          ]}
        />
      </MasterLayout>
    );
  }

  const rawData = parseJsonData(domainInfo.rawData);
  const contacts = parseJsonData(domainInfo.contacts);
  const nameservers = parseJsonData(domainInfo.nameservers);
  const livednsInfo = parseJsonData(domainInfo.livednsInfo);

  return (
    <MasterLayout>
      <Breadcrumb
        title={domainInfo.fqdn}
        subtitle={
          <>
            Détail domaine Gandi
            {!apiWorking && (
              <span className="badge bg-warning ms-2">Mode test</span>
            )}
          </>
        }
        actions={toolsMenu}
      />

      {error && (
        <AppAlert variant="warning" className="py-2">
          <strong>Mode test :</strong> {error}
        </AppAlert>
      )}

      <div className="row">
          {/* Informations générales */}
          <div className="col-lg-8">
            <div className="card mb-4">
              <div className="card-header py-2">
                <h5 className="card-title mb-0 text-sm">
                  <i className="ri-information-line me-2"></i>
                  Informations générales
                </h5>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label fw-bold">Nom de domaine</label>
                      <p className="form-control-plaintext">{domainInfo.fqdn}</p>
                    </div>
                    <div className="mb-3">
                      <label className="form-label fw-bold">Statut</label>
                      <div>
                        <span className={`badge ${domainInfo.statusClass}`}>
                          {domainInfo.statusText}
                        </span>
                      </div>
                    </div>
                    <div className="mb-3">
                      <label className="form-label fw-bold">Jours restants</label>
                      <div>
                        <span className={`badge ${domainInfo.daysClass}`}>
                          {domainInfo.daysText}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label fw-bold">Date d'expiration</label>
                      <p className="form-control-plaintext">
                        {formatDate(getGandiExpirationDate(domainInfo))}
                      </p>
                    </div>
                    <div className="mb-3">
                      <label className="form-label fw-bold">État du renouvellement</label>
                      <div>
                        <span className={`badge ${domainInfo.renewalClass}`}>
                          {domainInfo.renewalText}
                        </span>
                      </div>
                    </div>
                    <div className="mb-3">
                      <label className="form-label fw-bold">Organisation</label>
                      <p className="form-control-plaintext">
                        <span className="badge bg-info">
                          {domainInfo.organization || 'N/A'}
                        </span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Serveurs DNS */}
            <div className="card mb-4">
              <div className="card-header py-2">
                <h5 className="card-title mb-0 text-sm">
                  <i className="ri-dns-line me-2"></i>
                  Serveurs DNS
                </h5>
              </div>
              <div className="card-body">
                {nameservers && nameservers.length > 0 ? (
                  <div className="row">
                    {nameservers.map((ns, index) => (
                      <div key={index} className="col-md-6 mb-2">
                        <div className="d-flex align-items-center">
                          <i className="ri-server-line me-2 text-primary"></i>
                          <span className="fw-bold">{ns}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted">Aucun serveur DNS configuré</p>
                )}
              </div>
            </div>

            {/* Informations LiveDNS */}
            {livednsInfo && (
              <div className="card mb-4">
                <div className="card-header py-2">
                  <h5 className="card-title mb-0 text-sm">
                    <i className="ri-cloud-line me-2"></i>
                    Informations LiveDNS
                  </h5>
                </div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label fw-bold">Type de DNS</label>
                        <p className="form-control-plaintext">
                          <span className="badge bg-primary">
                            {livednsInfo.current || 'N/A'}
                          </span>
                        </p>
                      </div>
                      <div className="mb-3">
                        <label className="form-label fw-bold">DNSSEC disponible</label>
                        <div>
                          <span className={`badge ${livednsInfo.dnssec_available ? 'bg-success' : 'bg-secondary'}`}>
                            {livednsInfo.dnssec_available ? 'Oui' : 'Non'}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label fw-bold">LiveDNSSEC disponible</label>
                        <div>
                          <span className={`badge ${livednsInfo.livednssec_available ? 'bg-success' : 'bg-secondary'}`}>
                            {livednsInfo.livednssec_available ? 'Oui' : 'Non'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Informations de renouvellement */}
            {rawData?.basic?.autorenew && (
              <div className="card mb-4">
                <div className="card-header py-2">
                  <h5 className="card-title mb-0 text-sm">
                    <i className="ri-refresh-line me-2"></i>
                    Informations de renouvellement
                  </h5>
                </div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label fw-bold">Renouvellement automatique</label>
                        <div>
                          <span className={`badge ${rawData.basic.autorenew.enabled ? 'bg-success' : 'bg-warning'}`}>
                            {rawData.basic.autorenew.enabled ? 'Activé' : 'Désactivé'}
                          </span>
                        </div>
                      </div>
                      <div className="mb-3">
                        <label className="form-label fw-bold">Durée de renouvellement</label>
                        <p className="form-control-plaintext">
                          {rawData.basic.autorenew.duration || 'N/A'} an(s)
                        </p>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label fw-bold">Date de création</label>
                        <p className="form-control-plaintext">
                          {formatDate(rawData.basic.dates?.created_at)}
                        </p>
                      </div>
                      <div className="mb-3">
                        <label className="form-label fw-bold">Dernière mise à jour</label>
                        <p className="form-control-plaintext">
                          {formatDate(rawData.basic.dates?.updated_at)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Informations techniques */}
          <div className="col-lg-4">
            <div className="card mb-4">
              <div className="card-header py-2">
                <h5 className="card-title mb-0 text-sm">
                  <i className="ri-settings-3-line me-2"></i>
                  Informations techniques
                </h5>
              </div>
              <div className="card-body">
                <div className="mb-3">
                  <label className="form-label fw-bold">Statuts du domaine</label>
                  <div>
                    {domainInfo.status && Array.isArray(domainInfo.status) && domainInfo.status.length > 0 ? (
                      domainInfo.status.map((status, index) => (
                        <span key={index} className="badge bg-info me-1 mb-1">
                          {status}
                        </span>
                      ))
                    ) : (
                      <span className="badge bg-secondary">Aucun statut</span>
                    )}
                  </div>
                </div>
                <div className="mb-3">
                  <label className="form-label fw-bold">Dernière synchronisation</label>
                  <p className="form-control-plaintext">
                    {formatDate(domainInfo.lastSync)}
                  </p>
                </div>
              </div>
            </div>

            {/* Contacts */}
            {contacts && (
              <div className="card mb-4">
                <div className="card-header py-2">
                  <h5 className="card-title mb-0 text-sm">
                    <i className="ri-contacts-line me-2"></i>
                    Contacts
                  </h5>
                </div>
                <div className="card-body">
                  {contacts.owner && (
                    <div className="mb-3">
                      <label className="form-label fw-bold">Propriétaire</label>
                      <div className="small">
                        <div><strong>Organisation:</strong> {contacts.owner.orgname || 'N/A'}</div>
                        <div><strong>Nom:</strong> {contacts.owner.given} {contacts.owner.family}</div>
                        <div><strong>Email:</strong> {contacts.owner.email || 'N/A'}</div>
                        <div><strong>Téléphone:</strong> {contacts.owner.phone || 'N/A'}</div>
                      </div>
                    </div>
                  )}
                  {contacts.admin && (
                    <div className="mb-3">
                      <label className="form-label fw-bold">Administrateur</label>
                      <div className="small">
                        <div><strong>Organisation:</strong> {contacts.admin.orgname || 'N/A'}</div>
                        <div><strong>Nom:</strong> {contacts.admin.given} {contacts.admin.family}</div>
                        <div><strong>Email:</strong> {contacts.admin.email || 'N/A'}</div>
                        <div><strong>Téléphone:</strong> {contacts.admin.phone || 'N/A'}</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Tags et redirections */}
            <div className="card mb-4">
              <div className="card-header py-2">
                <h5 className="card-title mb-0 text-sm">
                  <i className="ri-price-tag-3-line me-2"></i>
                  Tags et redirections
                </h5>
              </div>
              <div className="card-body">
                <div className="mb-3">
                  <label className="form-label fw-bold">Tags</label>
                  <div>
                    {domainInfo.tags && Array.isArray(domainInfo.tags) && domainInfo.tags.length > 0 ? (
                      domainInfo.tags.map((tag, index) => (
                        <span key={index} className="badge bg-light text-dark me-1 mb-1">
                          {tag}
                        </span>
                      ))
                    ) : (
                      <span className="text-muted">Aucun tag</span>
                    )}
                  </div>
                </div>
                <div className="mb-3">
                  <label className="form-label fw-bold">Redirections web</label>
                  <div>
                    {domainInfo.webRedirections && Array.isArray(domainInfo.webRedirections) && domainInfo.webRedirections.length > 0 ? (
                      domainInfo.webRedirections.map((redir, index) => (
                        <div key={index} className="small mb-1">
                          <strong>{redir.host}</strong> → {redir.url} ({redir.type})
                        </div>
                      ))
                    ) : (
                      <span className="text-muted">Aucune redirection</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
    </MasterLayout>
  );
};

export default DomaineDetailGandiPage;
